import { useEffect, useRef, useState } from "react";
import { sendMessageToAPI } from "../../api/agenteIA/chatService";
import { getObrasRelatorioFvs, ObraRelatorioFvs } from "../../api/agenteIA/reportService";
import { useDependencyInjection } from "../../config/di/dependency_injection_context";
import { useUserAuthStore } from "../../stores/global/userAuth";
import { DefaultAutocomplete } from "./default_autocomplete";

const BotIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 8V4H8"/><rect width="16" height="12" x="4" y="8" rx="2"/><path d="M2 14h2"/><path d="M20 14h2"/><path d="M15 13v2"/><path d="M9 13v2"/>
  </svg>
);

// Ícone SVG: Utilizador
const UserIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
  </svg>
);

// Ícone SVG: Enviar
const SendIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m22 2-7 20-4-9-9-4Z"/><path d="M22 2 11 13"/>
  </svg>
);

const ChatInterface = () => {
  const { reports: { getReportFvsDinamicoUseCase } } = useDependencyInjection();
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Olá! Sou o teu assistente virtual. Como posso ajudar-te hoje?" }
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [reportError, setReportError] = useState("");
  const [obras, setObras] = useState<ObraRelatorioFvs[]>([]);
  const [selectedObra, setSelectedObra] = useState<ObraRelatorioFvs | null>(null);
  const [isLoadingObras, setIsLoadingObras] = useState(true);
  const idUsuario = useUserAuthStore(state => state.idUsuario);
  
  const [sessionId, setSessionId] = useState(""); 
  
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSessionId(crypto.randomUUID());
  }, []);

  useEffect(() => {
    let active = true;
    getObrasRelatorioFvs()
      .then(items => {
        if (active) setObras(items);
      })
      .catch(() => {
        if (active) setReportError("Não foi possível carregar as obras. Reabra a página para tentar novamente.");
      })
      .finally(() => {
        if (active) setIsLoadingObras(false);
      });
    return () => { active = false; };
  }, []);

  // Mantém a vista sempre na mensagem mais recente
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleGenerateReport = async () => {
    if (isGeneratingReport) return;
    if (!selectedObra) {
      setReportError("Selecione uma obra para gerar o relatório.");
      return;
    }

    setIsGeneratingReport(true);
    setReportError("");

    try {
      const html = await getReportFvsDinamicoUseCase.getReportFvsDinamico(selectedObra.idObra, idUsuario);
      const htmlBlob = new Blob([html], { type: "text/html;charset=utf-8" });
      const url = URL.createObjectURL(htmlBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "Relatorio_FVS.html";
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      setReportError(error instanceof Error ? error.message : "Não foi possível gerar o relatório. Tente novamente.");
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const handleSend = async (e: any) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = { role: "user", content: input.trim() };
    
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput("");
    setIsLoading(true);

    const aiResponse = await sendMessageToAPI(input.trim(), sessionId);
    
    setMessages((prev: any[]) => [...prev, aiResponse]);
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen w-full bg-slate-100 flex items-center justify-center p-4 sm:p-8 font-sans text-slate-800">
      
      <div className="w-full max-w-3xl h-[85vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-slate-200">
        
        <div className="bg-blue-600 text-white p-4 flex items-center gap-3 shadow-md z-10">
          <div className="bg-white/20 p-2 rounded-full">
            <BotIcon />
          </div>
          <div>
            <h1 className="font-bold text-lg leading-tight">Assistente IA</h1>
            <p className="text-blue-100 text-xs">Conectado e com memória ativa</p>
          </div>
        </div>

        <div className="px-4 py-3 border-b border-slate-200">
          <div className="mb-3">
            <DefaultAutocomplete
              label="Obra do relatório"
              placeholder={isLoadingObras ? "Carregando obras..." : "Selecione uma obra"}
              options={obras}
              value={selectedObra}
              getOptionKey={obra => obra.idObra}
              getOptionLabel={obra => obra.centroCusto || obra.empreendimento || `Obra ${obra.idObra}`}
              readOnly={isLoadingObras || isGeneratingReport}
              onChange={obra => { setSelectedObra(obra); setReportError(""); }}
            />
          </div>
          <button
            type="button"
            onClick={handleGenerateReport}
            disabled={isLoadingObras || isGeneratingReport || !selectedObra}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white px-4 py-2 rounded-xl transition-colors"
          >
            {isGeneratingReport ? "Gerando..." : "Gerar relatório"}
          </button>
          {reportError && (
            <p role="alert" className="mt-2 text-sm text-red-600">{reportError}</p>
          )}
        </div>

        {/* Área de Mensagens */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6 bg-slate-50">
          {messages.map((msg: any, index: any) => {
            const isUser = msg.role === "user";
            return (
              <div key={index} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                <div className={`flex max-w-[85%] sm:max-w-[75%] gap-3 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
                  
                 
                  <div className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center mt-1 ${isUser ? 'bg-blue-600 text-white' : 'bg-slate-300 text-slate-700'}`}>
                    {isUser ? <UserIcon /> : <BotIcon />}
                  </div>
                  
                  {/* Balão de Mensagem */}
                  <div className={`p-4 rounded-2xl shadow-sm ${
                    isUser 
                      ? 'bg-blue-600 text-white rounded-tr-none' 
                      : 'bg-white text-slate-700 border border-slate-200 rounded-tl-none'
                  }`}>
                    {/* Renderiza o texto respeitando quebras de linha */}
                    <p className="whitespace-pre-wrap text-sm leading-relaxed">
                      {msg.content}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
          
          {/* Indicador de "A escrever..." */}
          {isLoading && (
            <div className="flex justify-start">
              <div className="flex max-w-[80%] gap-3">
                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-slate-300 text-slate-700 flex items-center justify-center mt-1">
                  <BotIcon />
                </div>
                <div className="p-4 rounded-2xl rounded-tl-none bg-white border border-slate-200 shadow-sm flex items-center gap-1">
                  <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce"></span>
                  <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></span>
                  <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></span>
                </div>
              </div>
            </div>
          )}
          
          {/* Referência invisível para fazer scroll automático */}
          <div ref={messagesEndRef} />
        </div>

        {/* Área de Input */}
        <div className="p-4 bg-white border-t border-slate-200">
          <form onSubmit={handleSend} className="flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Escreve a tua mensagem aqui..."
              disabled={isLoading}
              className="flex-1 px-4 py-3 bg-slate-100 border border-transparent focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-200 rounded-xl outline-none transition-all disabled:opacity-50 text-slate-700"
            />
            <button 
              type="submit" 
              disabled={isLoading || !input.trim()}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white p-3 rounded-xl transition-colors flex items-center justify-center shadow-sm"
              title="Enviar mensagem"
            >
              <SendIcon />
            </button>
          </form>
        </div>

      </div>
    </div>
  );
};

export {ChatInterface};
